"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Camera,
  XCircle,
  Bell,
  Siren,
  ShieldCheck,
  QrCode,
  ThumbsUp,
  Ticket,
  PhoneCall,
  Calculator,
  FileBarChart,
  Megaphone,
  CreditCard,
  Receipt,
  CheckCircle2,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const PricingSection = () => {
  const containerRef = useRef(null);

  useGSAP(
    () => {
      gsap.from(".pricing-card-mid", {
        scale: 0.95,
        boxShadow: "0 0 0 rgba(0,0,0,0)",
        scrollTrigger: {
          trigger: ".pricing-grid",
          start: "top 70%",
          end: "bottom 70%",
          scrub: true,
        },
      });
    },
    { scope: containerRef },
  );

  return (
    <section ref={containerRef} className="py-16 md:py-32 bg-[#050505] px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-4xl md:text-6xl font-clash font-bold mb-4 text-white">
            Product{" "}
            <span className="text-transparent bg-clip-text bg-linear-to-r from-orange-500 to-amber-500">
              Strategy & Pricing{" "}
            </span>{" "}
            Tiers
          </h2>
          <p className="text-gray-400 text-lg font-medium">
            Honesty, Security & Convenience with Complete ERP & Financials
          </p>
        </div>
        <div className="pricing-grid grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {/* TIER 1 */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/5 rounded-3xl p-8 flex flex-col h-full transition-all hover:border-white/20 hover:shadow-[0_0_30px_rgba(255,255,255,0.05)]">
            <div className="mb-6">
              <h2 className="text-2xl font-clash font-semibold mb-2 text-white">
                Tier 1: Digital Log
              </h2>
              <p className="text-gray-400 text-sm">
                The &quot;Honesty&quot; Replacement.
              </p>
            </div>
            <div className="mb-8">
              <span className="text-4xl  font-clash font-bold text-white">
                FREE
              </span>
              <span className="text-gray-500 ml-2">/ Lifetime</span>
            </div>
            <p className="text-sm text-gray-300 mb-8 leading-relaxed">
              Replaces the physical register to build digital habits. Low
              friction entry point.
            </p>
            <div className="h-px bg-white/10 my-6" />
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-4">
              Guard App (Lite)
            </div>
            <ul className="space-y-4 mb-4">
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <Camera className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <span>Photo & Phone Capture Only.</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-500 opacity-60">
                <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <span>No Real-time Buzzing.</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-500 opacity-60">
                <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <span>No Approve/Deny Status.</span>
              </li>
            </ul>
            <div className="h-px bg-white/10 my-6" />
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-4">
              Resident App
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <Bell className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <span>Passive Entry Logs (&quot;Visitor Logged&quot;).</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-500 opacity-60">
                <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <span>Cannot Act on Notifications.</span>
              </li>
            </ul>
          </div>
          {/* TIER 2 */}
          <div className="pricing-card-mid bg-white/5 backdrop-blur-xl border-spacing-0 border-orange-500/40 rounded-2xl p-10 flex flex-col h-full transition-all relative overflow-hidden shadow-[0_0_40px_rgba(249,115,23,0.2)] md:scale-[1.08] z-10 border-2 md:border">
            <div className="absolute top-0 left-0 right-0 h-1 bg-linear-to-r from-orange-500 to-amber-500" />
            <div className="absolute top-3 right-5 px-3 py-1 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-full text-[10px] font-bold uppercase tracking-wider">
              Recommended
            </div>
            <div className="mb-6">
              <h2 className="text-2xl font-clash font-semibold mb-2 text-orange-400">
                Tier 2: The Gatekeeper
              </h2>
              <p className="text-gray-400 text-sm">
                Active Security & Convenience.
              </p>
            </div>
            <div className="mb-8">
              <span className="text-4xl font-clash font-bold text-white">
                ₹ PAID
              </span>
              <span className="text-gray-500 ml-2">/ Flat / Month</span>
            </div>
            <p className="text-sm text-gray-300 mb-8 leading-relaxed">
              Real-time verification loop creating dependence on the system for
              security.
            </p>
            <div className="h-px bg-white/10 my-6" />
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-4">
              Guard App (Pro)
            </div>
            <ul className="space-y-4 mb-4">
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <Siren className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                <span className="font-bold text-white">
                  IVR Buzzing triggers.
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <ShieldCheck className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                <span>Wait for Resident Approval.</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <QrCode className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                <span>Scan Pre-approved Passes.</span>
              </li>
            </ul>
            <div className="h-px bg-white/10 my-6" />
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-4">
              Resident App
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <ThumbsUp className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                <span className="font-bold text-white">
                  Instant Approve/Deny Actions.
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <Ticket className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                <span>Issue Guest/Staff Passes.</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <PhoneCall className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                <span>Receive IVR Security Calls.</span>
              </li>
            </ul>
          </div>
          {/* TIER 3 */}
          <div className="bg-white/5 backdrop-blur-xl border border-purple-500/40 rounded-3xl p-8 flex flex-col h-full transition-all hover:shadow-[0_0_40px_rgba(168,85,247,0.15)] relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-linear-to-r from-purple-500 to-yellow-500" />
            <div className="mb-6">
              <h2 className="text-2xl font-clash font-semibold mb-2 text-purple-400">
                Tier 3: Society OS
              </h2>
              <p className="text-gray-400 text-sm">
                Complete ERP & Financials.
              </p>
            </div>
            <div className="mb-8">
              <span className="text-4xl font-clash font-bold text-white">
                ₹ PREMIUM
              </span>
              <span className="text-gray-500 ml-2">/ Flat / Month</span>
            </div>
            <p className="text-sm text-gray-300 mb-8 leading-relaxed">
              Locks the committee in with critical financial data and automated
              workflows.
            </p>
            <div className="h-px bg-white/10 my-6" />
            <div className="flex items-center gap-2 mb-6 bg-purple-500/10 p-3 rounded-xl border border-purple-500/20">
              <CheckCircle2 className="w-5 h-5 text-purple-400" />
              <span className="text-sm font-clash font-semibold text-white">
                Includes all Tier 2 Security features.
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-purple-400 font-bold mb-4">
              Admin ERP Engine
            </div>
            <ul className="space-y-4 mb-4">
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <Calculator className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                <span className="font-bold text-white">
                  Automated Billing (MCS Act).
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <FileBarChart className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                <span>Financial Reports & Arrears.</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <Megaphone className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                <span>Official Notices & Helpdesk.</span>
              </li>
            </ul>
            <div className="h-px bg-white/10 my-6" />
            <div className="text-[10px] uppercase tracking-widest text-purple-400 font-bold mb-4">
              Resident Finance
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <CreditCard className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                <span className="font-bold text-white">
                  In-App Bill Payment Gateway.
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-300">
                <Receipt className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                <span>Instant Payment Receipts.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
